/**
 * `spec.md` § Edge cases, `research.md` W-22: the server `Date` header, captured on load and
 * on every response, gives the offset relative times are computed from, so "2 minutes ago"
 * is correct under clock skew.
 */
export interface ClockOffset {
  /** Server time minus client time, in milliseconds; 0 until a `Date` header has been read. */
  readonly offsetMs: number;
  /** The server clock, as the client best knows it. */
  now(): Date;
  /** Read a response's `Date` header. Ignores a missing or unparseable header. */
  observe(headers: Headers, receivedAt?: number): void;
  subscribe(listener: (offsetMs: number) => void): () => void;
}

export function createClockOffset(clientNow: () => number = Date.now): ClockOffset {
  let offsetMs = 0;
  const listeners = new Set<(offsetMs: number) => void>();
  return {
    get offsetMs() {
      return offsetMs;
    },
    now: () => new Date(clientNow() + offsetMs),
    observe(headers, receivedAt = clientNow()) {
      const header = headers.get("Date");
      if (header === null) return;
      const serverAt = Date.parse(header);
      if (Number.isNaN(serverAt)) return;
      // The header has one-second resolution; an offset inside that resolution is noise.
      const next = serverAt - receivedAt;
      if (Math.abs(next - offsetMs) < 1000) return;
      offsetMs = next;
      for (const listener of listeners) listener(offsetMs);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}
